import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { FaEye } from "react-icons/fa";
import { Tooltip } from "@mui/material";
import { baseurl } from "../../config/url";
import PaymentUserProductDetails from "../paymentcomponents/paymentuserproductdetails";

import {
  FiSearch,
  FiAlertCircle,
  FiRefreshCw,
  FiTrendingUp,
  FiPackage,
  FiUsers,
} from "react-icons/fi";

const Transactions = ({ sellerId }) => {
  const [data, setData] = useState([]);
  const [showdetail, setshowdetail] = useState(false);
  const [details, setdetails] = useState({});

  // UI-only state
  const [query, setQuery] = useState("");
  const [uiLoading, setUiLoading] = useState(true);
  const [uiError, setUiError] = useState("");

  const fetchData = async () => {
    setUiLoading(true);
    setUiError("");

    const token = localStorage.getItem("token");
    const sid = sellerId || localStorage.getItem("sellerId");

    try {
      const response = await axios.get(
        `${baseurl}/api/payment/get-all-sales/${sid}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setData(response?.data?.sales || []);
    } catch (err) {
      console.error("Error fetching sales data:", err);
      setUiError("Failed to load payment summary. Please try again.");
    } finally {
      setUiLoading(false);
    }
  };

  const handleViewUser = (entry) => {
    setdetails(entry);
    setshowdetail(true);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Always compute memos (NO conditional hooks)
  const safeList = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return safeList;

    return safeList.filter((entry) => {
      const product = (entry?.productId?.name || "").toLowerCase();
      const category = (entry?.productId?.category?.name || "").toLowerCase();
      const user = (entry?.userId?.name || "").toLowerCase();
      const date = entry?.createdAt
        ? dayjs(entry.createdAt).format("DD MMM YYYY").toLowerCase()
        : "";
      const price = String(entry?.price ?? "").toLowerCase();
      const qty = String(entry?.quantity ?? "").toLowerCase();
      const wogst = String(entry?.subtotalwithoutgst ?? "").toLowerCase();
      const wgst = String(entry?.subtotalwithgst ?? "").toLowerCase();

      return (
        product.includes(q) ||
        category.includes(q) ||
        user.includes(q) ||
        date.includes(q) ||
        price.includes(q) ||
        qty.includes(q) ||
        wogst.includes(q) ||
        wgst.includes(q)
      );
    });
  }, [safeList, query]);

  const totals = useMemo(() => {
    const revenueWithoutGST = safeList.reduce(
      (sum, e) => sum + Number(e?.subtotalwithoutgst || 0),
      0
    );
    const totalWithGST = safeList.reduce(
      (sum, e) => sum + Number(e?.subtotalwithgst || 0),
      0
    );
    const totalQty = safeList.reduce(
      (sum, e) => sum + Number(e?.quantity || 0),
      0
    );

    const users = new Set(
      safeList
        .map((e) => e?.userId?._id || e?.userId?.email || e?.userId?.name)
        .filter(Boolean)
    );

    return {
      revenueWithoutGST,
      totalWithGST,
      totalQty,
      uniqueUsers: users.size,
      count: safeList.length,
    };
  }, [safeList]);

  // ✅ Render detail screen (no hook issue now)
  if (showdetail) {
    return (
      <PaymentUserProductDetails setshowdetail={setshowdetail} data={details} />
    );
  }

  // Loading UI
  if (uiLoading) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-slate-50 px-4">
        <div className="w-full max-w-6xl bg-white border rounded-3xl p-6 sm:p-7 shadow-sm">
          <div className="animate-pulse space-y-4">
            <div className="h-7 w-56 bg-slate-200 rounded" />
            <div className="h-4 w-96 bg-slate-200 rounded" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="h-20 bg-slate-200 rounded-3xl" />
              <div className="h-20 bg-slate-200 rounded-3xl" />
              <div className="h-20 bg-slate-200 rounded-3xl" />
              <div className="h-20 bg-slate-200 rounded-3xl" />
            </div>
            <div className="h-11 w-full bg-slate-200 rounded-2xl" />
            <div className="h-72 w-full bg-slate-200 rounded-3xl" />
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Loading payment summary…
          </p>
        </div>
      </div>
    );
  }

  // Error UI
  if (uiError) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-slate-50 px-4">
        <div className="w-full max-w-2xl bg-white border rounded-3xl p-6 sm:p-7 shadow-sm">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-50 border border-red-100 px-3 py-1 text-red-700 font-extrabold text-sm">
            <FiAlertCircle />
            Error
          </div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-3">
            Unable to load
          </h2>
          <p className="text-slate-600 mt-2">{uiError}</p>
          <button
            onClick={fetchData}
            className="mt-5 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-2xl bg-slate-900 text-white font-extrabold hover:bg-slate-800 transition"
          >
            <FiRefreshCw />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
              Payment Summary
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Breakdown of payments received for each product and the
              corresponding user.
            </p>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full sm:w-auto">
            <div className="rounded-3xl border bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-extrabold text-slate-500">
                  Sales
                </p>
                <FiTrendingUp className="text-slate-400" />
              </div>
              <p className="text-lg font-extrabold text-slate-900">
                {totals.count}
              </p>
            </div>

            <div className="rounded-3xl border bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-extrabold text-slate-500">
                  Quantity
                </p>
                <FiPackage className="text-slate-400" />
              </div>
              <p className="text-lg font-extrabold text-slate-900">
                {totals.totalQty}
              </p>
            </div>

            <div className="rounded-3xl border bg-white px-4 py-3 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-extrabold text-slate-500">
                  Users
                </p>
                <FiUsers className="text-slate-400" />
              </div>
              <p className="text-lg font-extrabold text-slate-900">
                {totals.uniqueUsers}
              </p>
            </div>

            <div className="rounded-3xl border bg-white px-4 py-3 shadow-sm">
              <p className="text-[11px] font-extrabold text-slate-500">
                Total (Incl. GST)
              </p>
              <p className="text-lg font-extrabold text-slate-900">
                ₹{Number(totals.totalWithGST || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Search + actions */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-5">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                <FiSearch />
              </span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search product, category, user, amount, date…"
                className="w-full h-11 pl-10 pr-4 rounded-2xl border bg-white text-slate-900 placeholder:text-slate-400
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3">
              {query?.trim() ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="w-full sm:w-auto inline-flex items-center justify-center h-11 px-4 rounded-2xl border bg-white
                             text-slate-900 font-extrabold hover:bg-slate-50 transition"
                >
                  Clear
                </button>
              ) : null}

              <button
                type="button"
                onClick={fetchData}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-11 px-4 rounded-2xl bg-slate-900 text-white font-extrabold
                           hover:bg-slate-800 transition"
              >
                <FiRefreshCw />
                Refresh
              </button>
            </div>
          </div>

          {/* Quick totals row */}
          <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            <p className="text-xs text-slate-500">
              Showing{" "}
              <span className="font-extrabold text-slate-700">
                {filtered.length}
              </span>{" "}
              of{" "}
              <span className="font-extrabold text-slate-700">
                {totals.count}
              </span>{" "}
              sales
            </p>
            <p className="text-xs text-slate-500">
              Revenue (Excl. GST):{" "}
              <span className="font-extrabold text-slate-700">
                ₹{Number(totals.revenueWithoutGST || 0).toFixed(2)}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {safeList.length > 0 ? (
          <div className="bg-white border rounded-3xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <th className="py-3 px-5 sm:px-6 text-[11px] font-extrabold tracking-wide text-slate-600">
                      Image
                    </th>
                    <th className="py-3 px-5 sm:px-6 text-[11px] font-extrabold tracking-wide text-slate-600">
                      Product
                    </th>
                    <th className="py-3 px-5 sm:px-6 text-[11px] font-extrabold tracking-wide text-slate-600">
                      Category
                    </th>
                    <th className="py-3 px-5 sm:px-6 text-[11px] font-extrabold tracking-wide text-slate-600">
                      User
                    </th>
                    <th className="py-3 px-5 sm:px-6 text-[11px] font-extrabold tracking-wide text-slate-600">
                      Price
                    </th>
                    <th className="py-3 px-5 sm:px-6 text-[11px] font-extrabold tracking-wide text-slate-600">
                      Qty
                    </th>
                    <th className="py-3 px-5 sm:px-6 text-[11px] font-extrabold tracking-wide text-slate-600">
                      Revenue (Excl. GST)
                    </th>
                    <th className="py-3 px-5 sm:px-6 text-[11px] font-extrabold tracking-wide text-slate-600">
                      <Tooltip
                        title="This is the total amount including GST."
                        arrow
                        placement="top"
                      >
                        <span className="cursor-help">Total (Incl. GST)</span>
                      </Tooltip>
                    </th>
                    <th className="py-3 px-5 sm:px-6 text-[11px] font-extrabold tracking-wide text-slate-600">
                      Purchased
                    </th>
                    <th className="py-3 px-5 sm:px-6 text-right text-[11px] font-extrabold tracking-wide text-slate-600">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((entry, index) => {
                    const imgOk = Boolean(entry?.productId?.image?.data);
                    const productName = entry?.productId?.name ?? "N/A";
                    const categoryName =
                      entry?.productId?.category?.name ?? "N/A";
                    const userName = entry?.userId?.name ?? "N/A";
                    const price =
                      entry?.price !== undefined
                        ? `₹${Number(entry.price).toFixed(2)}`
                        : "N/A";
                    const qty =
                      entry?.quantity !== undefined ? entry.quantity : "N/A";
                    const wogst =
                      entry?.subtotalwithoutgst !== undefined
                        ? `₹${Number(entry.subtotalwithoutgst).toFixed(2)}`
                        : "N/A";
                    const wgst =
                      entry?.subtotalwithgst !== undefined
                        ? `₹${Number(entry.subtotalwithgst).toFixed(2)}`
                        : "N/A";
                    const purchasedDate = entry?.createdAt
                      ? dayjs(entry.createdAt).format("DD MMM YYYY")
                      : "N/A";

                    return (
                      <tr
                        key={entry._id}
                        className={`border-t ${
                          index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                        } hover:bg-blue-50/40 transition-colors`}
                      >
                        <td className="py-3 px-5 sm:px-6">
                          {imgOk ? (
                            <img
                              src={`data:${entry?.productId?.image?.contentType};base64,${entry?.productId?.image?.data}`}
                              alt={productName}
                              className="w-12 h-12 object-cover rounded-2xl border shadow-sm"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-2xl border bg-slate-50 grid place-items-center text-[10px] font-extrabold text-slate-400">
                              N/A
                            </div>
                          )}
                        </td>

                        <td className="py-3 px-5 sm:px-6">
                          <p className="text-sm font-extrabold text-slate-900">
                            {productName}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            ID: {String(entry?._id || "").slice(0, 10)}…
                          </p>
                        </td>

                        <td className="py-3 px-5 sm:px-6">
                          <span className="inline-flex items-center rounded-full border bg-white px-3 py-1 text-xs font-extrabold text-slate-700">
                            {categoryName}
                          </span>
                        </td>

                        <td className="py-3 px-5 sm:px-6">
                          <p className="text-sm font-extrabold text-slate-900">
                            {userName}
                          </p>
                        </td>

                        <td className="py-3 px-5 sm:px-6 text-sm font-extrabold text-slate-900">
                          {price}
                        </td>

                        <td className="py-3 px-5 sm:px-6 text-sm font-extrabold text-slate-900">
                          {qty}
                        </td>

                        <td className="py-3 px-5 sm:px-6">
                          <p className="text-sm font-extrabold text-orange-700">
                            {wogst}
                          </p>
                        </td>

                        <td className="py-3 px-5 sm:px-6">
                          <p className="text-sm font-extrabold text-emerald-700">
                            {wgst}
                          </p>
                        </td>

                        <td className="py-3 px-5 sm:px-6 text-sm font-semibold text-slate-700">
                          {purchasedDate}
                        </td>

                        <td className="py-3 px-5 sm:px-6 text-right">
                          <Tooltip title="View Details" arrow placement="top">
                            <button
                              onClick={() => handleViewUser(entry)}
                              className="inline-flex items-center justify-center h-10 w-10 rounded-2xl
                                         bg-blue-600 text-white hover:bg-blue-700 transition shadow-sm"
                              aria-label="View Details"
                            >
                              <FaEye className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })}

                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={10} className="py-12 px-6 text-center">
                        <p className="text-sm font-extrabold text-slate-900">
                          No results found
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Try searching by product name, category, user, amount,
                          or date.
                        </p>
                        <button
                          type="button"
                          onClick={() => setQuery("")}
                          className="mt-4 inline-flex items-center justify-center h-10 px-4 rounded-2xl border bg-white
                                     text-slate-900 font-extrabold hover:bg-slate-50 transition"
                        >
                          Clear search
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-5 sm:px-6 py-4 border-t bg-white">
              <p className="text-xs text-slate-500">
                Tip: Click the{" "}
                <span className="font-extrabold text-slate-700">eye</span> button
                to open user & product payment details.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white border rounded-3xl shadow-sm p-8 text-center">
            <p className="text-lg font-extrabold text-slate-900">
              No sales data available.
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Once a user completes a purchase, transactions will appear here.
            </p>
            <button
              onClick={fetchData}
              className="mt-5 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-2xl bg-slate-900 text-white font-extrabold hover:bg-slate-800 transition"
            >
              <FiRefreshCw />
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transactions;